'use client';

import { cn } from '@/lib/ui/cn';
import type { BlockContentCardsProps } from '@/types/blocks';
import useEmblaCarousel from 'embla-carousel-react';
import * as React from 'react';

const sectionBg = {
  light: 'bg-sh-paper',
  dark: 'bg-sh-tan',
  teal: 'bg-sh-teal',
  cream: 'bg-sh-cream',
  slate: 'bg-sh-slate',
} as const;

export const ContentCards = ({
  heading,
  backgroundColor = 'light',
  children,
  className,
  ...rest
}: BlockContentCardsProps) => {
  const [emblaRef, embla] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    watchDrag: api => api.scrollSnapList().length > 1,
  });
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [snaps, setSnaps] = React.useState<number[]>([]);
  const count = React.Children.count(children);

  React.useEffect(() => {
    if (!embla) return;

    const update = () => {
      setSnaps(embla.scrollSnapList());
      setSelectedIndex(embla.selectedScrollSnap());
    };
    const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());

    update();
    embla.on('reInit', update);
    embla.on('select', onSelect);

    return () => {
      embla.off('reInit', update);
      embla.off('select', onSelect);
    };
  }, [embla, count]);

  return (
    <section
      data-component='content-cards'
      data-test='content-cards'
      className={cn('py-10 lg:py-14', sectionBg[backgroundColor], className)}
      {...rest}
    >
      {heading && (
        <h2 className='text-sh-dark-gray mb-6 px-4 text-center font-serif text-2xl leading-none font-light tracking-[-0.02em] lg:mb-10 lg:text-4xl lg:leading-[130%] lg:tracking-normal'>
          {heading}
        </h2>
      )}
      <div className='mx-auto max-w-241 lg:px-4'>
        <div
          ref={emblaRef}
          className='relative overflow-hidden lg:-mx-px lg:px-px'
        >
          <div className='flex touch-pan-y touch-pinch-zoom gap-4 pl-4 lg:pl-0'>
            {React.Children.map(children, (child, i) => (
              <div
                role='group'
                aria-roledescription='slide'
                aria-label={`${i + 1} of ${count}`}
                className='min-w-0 shrink-0 basis-75 select-none last:mr-4 lg:last:mr-0'
              >
                {child}
              </div>
            ))}
          </div>
        </div>
        {snaps.length > 1 && (
          <div className='mt-6 flex justify-center gap-1'>
            {snaps.map((_, i) => (
              <button
                key={i}
                type='button'
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === selectedIndex}
                onClick={() => embla?.scrollTo(i)}
                className='flex size-6 cursor-pointer items-center justify-center'
              >
                <span
                  className={cn(
                    'border-sh-dark-gray size-2.5 rounded-full border',
                    i === selectedIndex && 'bg-sh-dark-gray'
                  )}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
